import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, Tabs, Spin, message } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import { confirmAlert } from 'react-confirm-alert';
import POSLayoutB2 from './POSLayoutB2';
import SalesReportB2 from './SalesReportB2';
import DeliveryReportB2 from '../Inventory/DeliveryReportB2';
import CurrentInventoryB2 from '../Inventory/CurrentInventoryB2';
import InventoryReportB2 from '../Inventory/InventoryReportB2';
import InventoryPageB2 from '../Inventory/InventoryPageB2';

const { Content } = Layout;

function ModernPOS() {
  const [activeTab, setActiveTab] = useState('pos');

  const [students, setStudents] = useState([]);
  const [items, setItems] = useState([]);
  const [bankDetails, setBankDetails] = useState([]);

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [cartItems, setCartItems] = useState([]);

  const [discount, setDiscount] = useState(0);
  const [vatChecked, setVatChecked] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [note, setNote] = useState('');
  const [paymentDate, setPaymentDate] = useState(dayjs());


  const [loading, setLoading] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(false);

  const categories = useMemo(
  () => [
    'All',
    'Registration Fee',
    'A Level CIE',
    'A Level Edexcel',
    'ACT',
    'AP',
    'Cambridge English',
    'Cambridge Primary',
    'Cambridge Lower Secondary',
    'Checkpoint',
    'Checkpoint SPIP',
    'Chemistry',
    'CU Exam',
    'English',
    'English Literacy',
    'GED',
    'GMAT',
    'IB',
    'IELTS',
    'IGCSE AQA',
    'IGCSE CIE',
    'IGCSE Edexcel',
    'ISEB',
    'Key Stage',
    'KMIDS',
    'MAP Test',
    'Math',
    'Math Olympiad',
    'MYP',
    'Physics',
    'PSAT',
    'SAT',
    'Science',
    'SSAT',
    'Summer English',
    'Summer Math',
    'Test Fee',
    'Thai',
    'TOEFL',
    'TOEIC',
    'TU-GET',
    'White Rose Maths',
    'Writing',
    'Original Book',
    'Others'
  ],
  []
);

  const [selectedCategory, setSelectedCategory] = useState('All');

  const API_URL = process.env.REACT_APP_API_URL;

  const formatPrice = price => Number(price).toFixed(2);

  // Fetch functions (STABLE)
const fetchStudents = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const { data } = await axios.get(`${API_URL}/students/pos-students`, {
      headers: { Authorization: `Bearer ${token}` }
    });


    setStudents(data.students || []);

  } catch (error) {
    console.error("FETCH STUDENTS ERROR:", error);
    message.error('Failed to fetch students');
  }
}, [API_URL]);

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/inventory-b2`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(data.filter(item => item.status === 'active'));
    } catch (error) {
      message.error('Failed to fetch items');
    }
  }, [API_URL]);

  const fetchBankDetails = useCallback(async () => {
    try {
      setLoadingBanks(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankDetails(data);
    } catch (error) {
      message.error('Failed to fetch bank details');
    } finally {
      setLoadingBanks(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchStudents();
    fetchItems();
    fetchBankDetails();
  }, []);

  // Pure functions
  const subtotal = useMemo(() => cartItems.reduce((sum, i) => sum + i.price_per_unit * i.quantity, 0), [cartItems]);
  const vat = useMemo(() => vatChecked ? (subtotal - discount) * 0.07 : 0, [subtotal, discount, vatChecked]);
  const total = useMemo(() => subtotal - discount + vat, [subtotal, discount, vat]);

  const filteredItems = useMemo(() =>
    items.filter(i => selectedCategory === 'All' || i.category === selectedCategory),
  [items, selectedCategory]);

  // Callbacks
  const handleDiscountChange = useCallback(e => setDiscount(parseFloat(e.target.value) || 0), []);
  const handleVatChange = useCallback(e => setVatChecked(e.target.checked), []);
  const handlePaymentMethodChange = useCallback(val => {
    setPaymentMethod(val);
    if (val !== 'bank_transfer') setSelectedBank(null);
  }, []);

  const addToCart = useCallback(item => {
    setCartItems(prev => {
      const exist = prev.find(i => i.inventory_id === item.inventory_id);
      if (exist) {
        return prev.map(i => i.inventory_id === item.inventory_id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  }, []);

  const updateQuantity = useCallback((id, qty) => {
    setCartItems(prev => prev.map(i => i.inventory_id === id ? { ...i, quantity: qty } : i));
  }, []);

  const removeFromCart = useCallback(id => {
    setCartItems(prev => prev.filter(i => i.inventory_id !== id));
  }, []);

  const isPurchaseDisabled = useCallback(() => {
    if (!cartItems.length || !selectedStudent || !paymentMethod) return true;
    if (paymentMethod === 'bank_transfer' && !selectedBank) return true;
    return false;
  }, [cartItems, selectedStudent, paymentMethod, selectedBank]);

  const handlePurchase = useCallback(async () => {
    if (!selectedStudent) return message.warning('Please select a student');
    if (!cartItems.length) return message.warning('Cart is empty');
    if (!paymentMethod) return message.warning('Please select payment method');
    
    // Check if bank transfer is selected but no bank is chosen
    if (paymentMethod === 'bank_transfer' && !selectedBank) {
      return message.warning('Please select a bank for bank transfer payment');
    }

    const saleData = {
      student_id: selectedStudent,
      items: cartItems,
      discount,
      vat,
      payment_method: paymentMethod,
      bank_detail_id: paymentMethod === 'bank_transfer' ? selectedBank : null,
      note,
      // Optional: unpaid sales have nothing to date, and it can be left blank here
      // and filled in later from Change Payment Method.
      payment_date: (paymentMethod === 'unpaid' || !paymentDate)
        ? null
        : paymentDate.format('YYYY-MM-DD'),
    };

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/sales-b2/log-sale-b2`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      confirmAlert({
        title: 'Success',
        message: 'Purchase completed successfully!',
        buttons: [{ label: 'OK', onClick: () => resetCart() }]
      });
    } catch (error) {
      message.error('Purchase failed');
    } finally {
      setLoading(false);
    }
  }, [cartItems, selectedStudent, paymentMethod, selectedBank, discount, vat, note, paymentDate]);



  const resetCart = () => {
    setSelectedStudent(null);
    setCartItems([]);
    setDiscount(0);
    setVatChecked(false);
    setPaymentMethod(null);
    setSelectedBank(null);
    setNote("");
    setPaymentDate(dayjs());
  };
  const handleRefresh = useCallback(() => {
    setCartItems([]);
    setSelectedStudent(null);
    setSelectedCategory('All');
    setDiscount(0);
    setVatChecked(false);
    setPaymentMethod(null);
    setSelectedBank(null);
    setNote("");
    setPaymentDate(dayjs());
    fetchStudents();
    fetchItems();
    fetchBankDetails();
  }, [fetchStudents, fetchItems, fetchBankDetails]);

  // Memoized tabs content
  const tabsContent = useMemo(() => ([
    { key: 'pos', label: <>POS</>, children:
      <POSLayoutB2
        {...{
          students, items, filteredItems, selectedStudent, setSelectedStudent,
          cartItems, setCartItems, updateQuantity, removeFromCart,
          discount, handleDiscountChange, vatChecked, handleVatChange,
          subtotal, vat, total, selectedCategory, setSelectedCategory,
          categories, formatPrice, paymentMethod, handlePaymentMethodChange,
          selectedBank, setSelectedBank, bankDetails, loadingBanks,
          handlePurchase, isPurchaseDisabled, handleRefresh, addToCart, note, setNote,
          paymentDate, setPaymentDate
        }}
      />
    },
    { key: 'salesReport', label: 'Sales Report', children: <SalesReportB2 /> },
    { key: 'deliveryReport', label: 'Delivery Report', children: <DeliveryReportB2 /> },
    { key: 'currentInventory', label: 'Current Inventory', children: <CurrentInventoryB2 /> },
    { key: 'report', label: 'Inventory Report', children: <InventoryReportB2 /> },
    { key: 'add', label: 'Add Inventory', children: <InventoryPageB2 /> },
  ]), [students, items, filteredItems, selectedStudent, cartItems, discount, vatChecked, subtotal, vat, total, selectedCategory, paymentMethod, selectedBank, bankDetails, loadingBanks, note, paymentDate]);

  return (
    <Layout className="pos-system">
      <Sidebar />
      <Layout>
        <Header />
        <Content>
          <Tabs className="pos-tabs" activeKey={activeTab} onChange={setActiveTab} items={tabsContent} />
        </Content>
      </Layout>
    </Layout>
  );
}

export default withRoleAccess(ModernPOS, ['superadmin','admin']);
